/**
 * Shared content primitives for detail drawers (Finance, Tariffs, Users, Stations, Powerbanks, Rents).
 * Use with DetailDrawer for a unified look: Section (one Paper per block), Field/CopyableField (no per-field Paper).
 * Copyable values are clickable (Button text) to copy — no separate copy icon.
 *
 * Layout is label-above-value so long IDs/coords wrap inside the drawer on narrow phones
 * instead of forcing horizontal overflow / Safari zoom.
 */
import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

const valueSx = {
  textTransform: 'none',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  minWidth: 0,
  maxWidth: '100%',
  flex: '1 1 auto',
  justifyContent: 'flex-start',
  py: 0,
  px: 0,
  minHeight: 'auto',
  lineHeight: 1.43,
  textAlign: 'left',
};

export function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2, minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 1.5, bgcolor: 'action.hover', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}
      >
        {children}
      </Paper>
    </Box>
  );
}

export function Field({ label, value }) {
  const isReactNode = value != null && typeof value === 'object' && React.isValidElement(value);
  const display = isReactNode ? value : (value != null && value !== '' ? String(value) : '—');
  return (
    <Box sx={{ py: 0.75, minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {isReactNode ? (
        <Typography component="div" variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {display}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {display}
        </Typography>
      )}
    </Box>
  );
}

/** Clickable value copies on click (Button text). No copy icon. */
export function CopyableField({ label, value, onCopy, copyTooltip }) {
  const raw = value != null && value !== '' ? String(value) : '—';
  const copyable = value != null && value !== '';
  return (
    <Box sx={{ py: 0.75, minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {copyable && onCopy && copyTooltip != null ? (
        <Tooltip title={copyTooltip}>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => onCopy(String(value))}
            aria-label={copyTooltip}
            sx={valueSx}
          >
            {raw}
          </Button>
        </Tooltip>
      ) : (
        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
          {raw}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Copyable row with optional "Open in…" action. Value copies on click (no copy icon); optional Open-in button kept.
 */
export function CopyableFieldWithAction({ label, value, onCopy, onOpen, copyTooltip, openTooltip }) {
  const raw = value != null && value !== '' ? String(value) : '—';
  const hasValue = value != null && value !== '';
  const copyable = hasValue && onCopy && copyTooltip != null;
  return (
    <Box sx={{ py: 0.75, minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
        {copyable ? (
          <Tooltip title={copyTooltip}>
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={() => onCopy(String(value))}
              aria-label={copyTooltip}
              sx={valueSx}
            >
              {raw}
            </Button>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ flex: '1 1 auto', wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>
            {raw}
          </Typography>
        )}
        {hasValue && onOpen && openTooltip != null && (
          <Tooltip title={openTooltip}>
            <IconButton size="small" aria-label={openTooltip} onClick={onOpen} sx={{ flexShrink: 0 }}>
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

/**
 * Row with label + value (clickable to copy) + optional action button. No copy icon.
 */
export function FieldWithActions({ label, value, onCopy, copyTooltip, actionButton }) {
  const raw = value != null && value !== '' ? String(value) : '—';
  const copyable = value != null && value !== '' && onCopy && copyTooltip != null;
  return (
    <Box sx={{ py: 0.75, minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
        {copyable ? (
          <Tooltip title={copyTooltip}>
            <Button
              variant="text"
              color="primary"
              size="small"
              onClick={() => onCopy(String(value))}
              aria-label={copyTooltip}
              sx={valueSx}
            >
              {raw}
            </Button>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ flex: '1 1 auto', wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 }}>
            {raw}
          </Typography>
        )}
        {actionButton && <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>{actionButton}</Stack>}
      </Box>
    </Box>
  );
}
